import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, KeyRound, Link2, Plus, RefreshCw, ShieldAlert, Terminal, UserCheck, UserX } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { useHubToast } from "@/components/ui/HubToast";
import { Input, Label } from "@/components/ui/Input";
import { CardTitleWithHelp, HoverHintWithGlyph, PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import { isIntegratorAdmin, useIntegratorMe } from "@/lib/me-query";

type ServerAccessTier = "investigate" | "operator" | "admin";

interface ServerAccessHostRow {
  id: number;
  host_key: string;
  display_name: string;
  hostname_or_ip: string;
  ssh_port: number;
  jump_host_key: string | null;
  environment: string;
  notes: string | null;
  has_sync_token: boolean;
  last_synced_at: string | null;
  last_reported_payload: {
    reportedAt?: string;
    status?: string;
    appliedUsers?: string[];
    revokedUsers?: string[];
    unmanagedHumanUsers?: string[];
    sgsadminKeyCount?: number | null;
  } | null;
  active: boolean;
}

interface ServerAccessPersonRow {
  id: number;
  email: string;
  display_name: string;
  linux_username: string;
  staff_id: number | null;
  ssh_public_key: string | null;
  ssh_key_fingerprint: string | null;
  ssh_key_updated_at: string | null;
  has_invite_token: boolean;
  invite_expires_at: string | null;
  active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface ServerAccessGrantRow {
  id: number;
  person_id: number;
  host_id: number;
  host_key: string;
  access_tier: ServerAccessTier;
  enabled: boolean;
  expires_at: string | null;
  revoked_at: string | null;
  ticket_or_reason: string | null;
  updated_at: string;
}

interface ServerAccessAuditRow {
  id: number;
  actor_user_id: string | null;
  actor_label: string | null;
  action: string;
  person_id: number | null;
  host_id: number | null;
  details: Record<string, unknown>;
  created_at: string;
}

interface OverviewResponse {
  hosts: ServerAccessHostRow[];
  people: ServerAccessPersonRow[];
  grants: ServerAccessGrantRow[];
  auditLog: ServerAccessAuditRow[];
}

const PAGE_HOVER_HINT =
  "Control personal SSH accounts and permissions across Linux servers (godaddy-server, cloudlink, and future boxes). Floor PCs stay on MeshCentral and Android tablets stay on MDM. Break-glass account sgsadmin is never modified by Hub sync.";

function suggestLinuxUsername(email: string): string {
  const local = email.split("@")[0]?.toLowerCase() ?? "";
  const cleaned = local.replace(/[^a-z0-9_-]/g, "");
  if (/^[a-z][a-z0-9_-]{1,30}$/.test(cleaned)) {
    return cleaned;
  }
  return cleaned ? `u_${cleaned.slice(0, 28)}` : "";
}

function formatShortTimestamp(iso: string | null): string {
  if (!iso) {
    return "Never";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString();
}

export function ServerAccessPage() {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const meQuery = useIntegratorMe();
  const isAdmin = useMemo(() => isIntegratorAdmin(meQuery.data?.user), [meQuery.data?.user]);

  const [showAddPersonModal, setShowAddPersonModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newLinuxUsername, setNewLinuxUsername] = useState("");
  const [newSshPublicKey, setNewSshPublicKey] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [newGrantTierByHostId, setNewGrantTierByHostId] = useState<Record<number, ServerAccessTier | "none">>({});

  const [editingKeyPerson, setEditingKeyPerson] = useState<ServerAccessPersonRow | null>(null);
  const [editingKeyInput, setEditingKeyInput] = useState("");

  const [instructionsModal, setInstructionsModal] = useState<{
    person: ServerAccessPersonRow;
    instructions: string;
    inviteUrl: string | null;
  } | null>(null);

  const [hostSetupModal, setHostSetupModal] = useState<{
    hostKey: string;
    syncToken: string;
    installCommand: string;
  } | null>(null);

  const [copiedLabel, setCopiedLabel] = useState<string | null>(null);

  const overviewQuery = useQuery({
    queryKey: ["user-admin", "server-access-overview"],
    queryFn: () => apiFetch<OverviewResponse>("/api/user-admin/server-access/overview"),
    enabled: isAdmin,
  });

  const hosts = overviewQuery.data?.hosts ?? [];
  const people = overviewQuery.data?.people ?? [];
  const grants = overviewQuery.data?.grants ?? [];
  const auditLog = overviewQuery.data?.auditLog ?? [];

  const grantsByPersonAndHost = useMemo(() => {
    const map = new Map<string, ServerAccessGrantRow>();
    for (const grant of grants) {
      map.set(`${grant.person_id}:${grant.host_id}`, grant);
    }
    return map;
  }, [grants]);

  function copyToClipboard(text: string, label: string) {
    void navigator.clipboard.writeText(text).then(() => {
      setCopiedLabel(label);
      showToast({ message: `${label} copied to clipboard.`, tone: "success" });
      setTimeout(() => {
        setCopiedLabel((current) => (current === label ? null : current));
      }, 2500);
    });
  }

  const createPersonMutation = useMutation({
    mutationFn: async () => {
      const grantPayload = hosts
        .map((host) => {
          const choice = newGrantTierByHostId[host.id] ?? (host.host_key === "godaddy-server" ? "investigate" : "none");
          if (choice === "none") {
            return null;
          }
          return {
            hostId: host.id,
            accessTier: choice,
            enabled: true,
          };
        })
        .filter((item): item is NonNullable<typeof item> => item != null);

      return apiFetch<{ ok: boolean; personId: number; inviteUrl: string | null }>(
        "/api/user-admin/server-access/people",
        {
          method: "POST",
          body: JSON.stringify({
            email: newEmail.trim(),
            displayName: newDisplayName.trim(),
            linuxUsername: newLinuxUsername.trim(),
            sshPublicKey: newSshPublicKey.trim() || undefined,
            notes: newNotes.trim() || undefined,
            generateInviteLink: true,
            grants: grantPayload,
          }),
        },
      );
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["user-admin", "server-access-overview"] });
      setShowAddPersonModal(false);
      setNewEmail("");
      setNewDisplayName("");
      setNewLinuxUsername("");
      setNewSshPublicKey("");
      setNewNotes("");
      showToast({ message: "Server access provisioned.", tone: "success" });
      if (result.personId) {
        const instructionsResponse = await apiFetch<{ instructions: string }>(
          `/api/user-admin/server-access/people/${result.personId}/instructions`,
          {
            query: result.inviteUrl ? { inviteUrl: result.inviteUrl } : undefined,
          },
        );
        const refreshed = await queryClient.fetchQuery({
          queryKey: ["user-admin", "server-access-overview"],
          queryFn: () => apiFetch<OverviewResponse>("/api/user-admin/server-access/overview"),
        });
        const person = refreshed.people.find((row) => row.id === result.personId);
        if (person) {
          setInstructionsModal({
            person,
            instructions: instructionsResponse.instructions,
            inviteUrl: result.inviteUrl,
          });
        }
      }
    },
    onError: (error) => {
      showToast({
        message: error instanceof Error ? error.message : "Could not provision server access.",
        tone: "error",
      });
    },
  });

  const updateGrantMutation = useMutation({
    mutationFn: (input: {
      personId: number;
      hostId: number;
      accessTier: ServerAccessTier;
      enabled: boolean;
      expiresAt: string | null;
    }) =>
      apiFetch("/api/user-admin/server-access/grants", {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["user-admin", "server-access-overview"] });
      showToast({ message: "Server permission updated.", tone: "success" });
    },
    onError: (error) => {
      showToast({
        message: error instanceof Error ? error.message : "Failed to update server permission.",
        tone: "error",
      });
    },
  });

  const updatePersonMutation = useMutation({
    mutationFn: (input: {
      personId: number;
      sshPublicKey?: string;
      clearSshPublicKey?: boolean;
      active?: boolean;
    }) =>
      apiFetch(`/api/user-admin/server-access/people/${input.personId}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["user-admin", "server-access-overview"] });
      setEditingKeyPerson(null);
      setEditingKeyInput("");
      showToast({ message: "Account updated.", tone: "success" });
    },
    onError: (error) => {
      showToast({
        message: error instanceof Error ? error.message : "Failed to update account.",
        tone: "error",
      });
    },
  });

  const openInstructionsMutation = useMutation({
    mutationFn: async (input: { person: ServerAccessPersonRow; mintFreshInvite: boolean }) => {
      let inviteUrl: string | null = null;
      if (input.mintFreshInvite || input.person.ssh_public_key == null) {
        const inviteResponse = await apiFetch<{ inviteUrl: string }>(
          `/api/user-admin/server-access/people/${input.person.id}/invite-link`,
          { method: "POST" },
        );
        inviteUrl = inviteResponse.inviteUrl;
      }
      const instructionsResponse = await apiFetch<{ instructions: string }>(
        `/api/user-admin/server-access/people/${input.person.id}/instructions`,
        {
          query: inviteUrl ? { inviteUrl } : undefined,
        },
      );
      return {
        person: input.person,
        instructions: instructionsResponse.instructions,
        inviteUrl,
      };
    },
    onSuccess: (payload) => {
      void queryClient.invalidateQueries({ queryKey: ["user-admin", "server-access-overview"] });
      setInstructionsModal(payload);
    },
    onError: (error) => {
      showToast({
        message: error instanceof Error ? error.message : "Could not load connection instructions.",
        tone: "error",
      });
    },
  });

  const rotateHostTokenMutation = useMutation({
    mutationFn: (hostId: number) =>
      apiFetch<{
        hostId: number;
        hostKey: string;
        syncToken: string;
        installCommand: string;
      }>(`/api/user-admin/server-access/hosts/${hostId}/rotate-sync-token`, {
        method: "POST",
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["user-admin", "server-access-overview"] });
      setHostSetupModal({
        hostKey: data.hostKey,
        syncToken: data.syncToken,
        installCommand: data.installCommand,
      });
      showToast({ message: `Generated sync token for ${data.hostKey}.`, tone: "success" });
    },
    onError: (error) => {
      showToast({
        message: error instanceof Error ? error.message : "Could not rotate host sync token.",
        tone: "error",
      });
    },
  });

  const peopleColumns = useMemo<HubDataTableColumn<ServerAccessPersonRow>[]>(() => {
    const baseCols: HubDataTableColumn<ServerAccessPersonRow>[] = [
      {
        id: "person",
        header: "Person",
        sortValue: (row) => row.display_name,
        cell: (row) => (
          <div className="space-y-0.5">
            <div className="flex items-center gap-2 font-medium text-slate-900">
              <span>{row.display_name}</span>
              {!row.active ? (
                <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[11px] font-medium text-rose-800">
                  Revoked
                </span>
              ) : null}
            </div>
            <div className="text-xs text-slate-500">{row.email}</div>
          </div>
        ),
      },
      {
        id: "linux_username",
        header: "Linux user",
        sortValue: (row) => row.linux_username,
        cell: (row) => (
          <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-800">
            {row.linux_username}
          </code>
        ),
      },
      {
        id: "ssh_key",
        header: "SSH public key",
        sortValue: (row) => row.ssh_key_fingerprint ?? "",
        cell: (row) => (
          <div className="flex items-center gap-2">
            {row.ssh_key_fingerprint ? (
              <span className="font-mono text-xs text-emerald-800" title={row.ssh_key_fingerprint}>
                {row.ssh_key_fingerprint.slice(0, 26)}…
              </span>
            ) : (
              <span className="text-xs text-amber-700">No key yet (pending invite)</span>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setEditingKeyPerson(row);
                setEditingKeyInput(row.ssh_public_key ?? "");
              }}
            >
              <KeyRound className="mr-1 h-3.5 w-3.5" />
              {row.ssh_key_fingerprint ? "Replace" : "Paste key"}
            </Button>
          </div>
        ),
      },
    ];

    const hostCols: HubDataTableColumn<ServerAccessPersonRow>[] = hosts.map((host) => ({
      id: `host_${host.id}`,
      header: host.host_key,
      sortValue: (row) => {
        const grant = grantsByPersonAndHost.get(`${row.id}:${host.id}`);
        if (!grant || !grant.enabled) return "none";
        return grant.access_tier;
      },
      cell: (row) => {
        const grant = grantsByPersonAndHost.get(`${row.id}:${host.id}`);
        const value: ServerAccessTier | "none" =
          grant && grant.enabled ? grant.access_tier : "none";
        const isExpired =
          grant?.enabled && grant.expires_at != null && new Date(grant.expires_at).getTime() < Date.now();

        return (
          <div className="space-y-1">
            <select
              aria-label={`${row.display_name} access on ${host.host_key}`}
              className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              value={value}
              disabled={!row.active || updateGrantMutation.isPending}
              onChange={(event) => {
                const chosen = event.target.value as ServerAccessTier | "none";
                if (chosen === "none") {
                  updateGrantMutation.mutate({
                    personId: row.id,
                    hostId: host.id,
                    accessTier: grant?.access_tier ?? "investigate",
                    enabled: false,
                    expiresAt: grant?.expires_at ?? null,
                  });
                } else {
                  updateGrantMutation.mutate({
                    personId: row.id,
                    hostId: host.id,
                    accessTier: chosen,
                    enabled: true,
                    expiresAt: grant?.expires_at ?? null,
                  });
                }
              }}
            >
              <option value="none">No access</option>
              <option value="investigate">Investigate (read-only + logs)</option>
              <option value="operator">Operator (docker + services)</option>
              <option value="admin">Admin (full sudo)</option>
            </select>
            {grant?.enabled && grant.expires_at ? (
              <div className={isExpired ? "text-[11px] font-medium text-rose-700" : "text-[11px] text-slate-500"}>
                {isExpired ? "Expired " : "Until "}
                {new Date(grant.expires_at).toISOString().slice(0, 10)}
              </div>
            ) : null}
          </div>
        );
      },
    }));

    const actionCol: HubDataTableColumn<ServerAccessPersonRow> = {
      id: "actions",
      header: "Instructions & lifecycle",
      sortValue: (row) => (row.active ? "active" : "revoked"),
      cell: (row) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => openInstructionsMutation.mutate({ person: row, mintFreshInvite: false })}
          >
            <Terminal className="mr-1 h-3.5 w-3.5" />
            Instructions
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => openInstructionsMutation.mutate({ person: row, mintFreshInvite: true })}
          >
            <Link2 className="mr-1 h-3.5 w-3.5" />
            Invite link
          </Button>
          {row.active ? (
            <Button
              size="sm"
              variant="danger"
              onClick={() =>
                updatePersonMutation.mutate({
                  personId: row.id,
                  active: false,
                })
              }
            >
              <UserX className="mr-1 h-3.5 w-3.5" />
              Deprovision
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                updatePersonMutation.mutate({
                  personId: row.id,
                  active: true,
                })
              }
            >
              <UserCheck className="mr-1 h-3.5 w-3.5" />
              Restore
            </Button>
          )}
        </div>
      ),
    };

    return [...baseCols, ...hostCols, actionCol];
  }, [hosts, grantsByPersonAndHost, updateGrantMutation, openInstructionsMutation, updatePersonMutation]);

  if (!meQuery.isLoading && !isAdmin) {
    return (
      <div className="space-y-4">
        <PageTitleWithHelp hoverHint={PAGE_HOVER_HINT}>Server access</PageTitleWithHelp>
        <Card>
          <CardBody className="text-sm text-slate-600">
            Integrator administrator access is required to manage server SSH permissions.
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PageTitleWithHelp hoverHint={PAGE_HOVER_HINT}>Server access</PageTitleWithHelp>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void overviewQuery.refetch()}
            disabled={overviewQuery.isFetching}
          >
            <RefreshCw className="mr-1.5 h-4 w-4" />
            Refresh
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              const defaults: Record<number, ServerAccessTier | "none"> = {};
              for (const host of hosts) {
                defaults[host.id] = host.host_key === "godaddy-server" ? "investigate" : "none";
              }
              setNewGrantTierByHostId(defaults);
              setShowAddPersonModal(true);
            }}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Provision person
          </Button>
        </div>
      </div>

      {/* Managed Servers Status Strip */}
      <div className="grid gap-3 md:grid-cols-2">
        {hosts.map((host) => {
          const report = host.last_reported_payload;
          const unmanaged = report?.unmanagedHumanUsers ?? [];
          return (
            <Card key={host.id}>
              <CardHeader className="flex flex-row items-center justify-between py-3">
                <CardTitleWithHelp
                  hoverHint={
                    host.notes ??
                    "Managed Linux server. Pulls desired accounts and SSH keys every 60 seconds using its host sync token."
                  }
                >
                  {`${host.display_name} (${host.host_key})`}
                </CardTitleWithHelp>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => rotateHostTokenMutation.mutate(host.id)}
                  disabled={rotateHostTokenMutation.isPending}
                >
                  {host.has_sync_token ? "Rotate sync token" : "Setup host sync"}
                </Button>
              </CardHeader>
              <CardBody className="space-y-1.5 text-xs text-slate-600">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    Host: <code className="font-mono text-slate-900">{host.hostname_or_ip}:{host.ssh_port}</code>
                    {host.jump_host_key ? (
                      <span className="ml-2 text-slate-500">(ProxyJump via {host.jump_host_key})</span>
                    ) : null}
                  </span>
                  <span>
                    Last sync:{" "}
                    <span className="font-medium text-slate-900">
                      {formatShortTimestamp(host.last_synced_at)}
                    </span>
                  </span>
                </div>
                {unmanaged.length > 0 ? (
                  <div className="flex items-center gap-1.5 rounded bg-amber-50 px-2 py-1 text-amber-900">
                    <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      Unmanaged Linux accounts on host: <code className="font-mono">{unmanaged.join(", ")}</code>
                    </span>
                  </div>
                ) : null}
              </CardBody>
            </Card>
          );
        })}
      </div>

      {/* People & Grants Matrix */}
      <HubDataTable<ServerAccessPersonRow>
        columns={peopleColumns}
        rows={people}
        rowKey={(row) => row.id}
        empty="No server access accounts provisioned yet."
      />

      {/* Recent Audit Log */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">Recent access audit log</CardTitle>
        </CardHeader>
        <CardBody className="max-h-56 overflow-y-auto divide-y divide-slate-100 text-xs">
          {auditLog.length === 0 ? (
            <div className="py-2 text-slate-500">No audit events recorded yet.</div>
          ) : (
            auditLog.slice(0, 15).map((entry) => (
              <div key={entry.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                <div>
                  <span className="font-medium text-slate-900">{entry.action}</span>{" "}
                  <span className="text-slate-600">by {entry.actor_label ?? "system"}</span>
                  {entry.details && Object.keys(entry.details).length > 0 ? (
                    <code className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700">
                      {JSON.stringify(entry.details)}
                    </code>
                  ) : null}
                </div>
                <span className="tabular-nums text-slate-500">{formatShortTimestamp(entry.created_at)}</span>
              </div>
            ))
          )}
        </CardBody>
      </Card>

      {/* Provision Person Modal */}
      {showAddPersonModal ? (
        <div
          data-hub-modal-overlay
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
        >
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-slate-900">Provision server SSH access</h2>
                <HoverHintWithGlyph hoverHint="Creates a dedicated Linux username for this person, assigns per-server tiers, and generates a one-time self-service key enrollment & connection guide link." />
              </div>
            </div>
            <div className="space-y-3 px-5 py-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="sa-email">Email</Label>
                  <Input
                    id="sa-email"
                    type="email"
                    placeholder="martin@cloudkite.io"
                    value={newEmail}
                    onChange={(event) => {
                      const val = event.target.value;
                      setNewEmail(val);
                      if (!newLinuxUsername) {
                        setNewLinuxUsername(suggestLinuxUsername(val));
                      }
                    }}
                  />
                </div>
                <div>
                  <Label htmlFor="sa-name">Display name</Label>
                  <Input
                    id="sa-name"
                    placeholder="Martin (Cloudkite)"
                    value={newDisplayName}
                    onChange={(event) => setNewDisplayName(event.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="sa-linux-user">Linux username</Label>
                <Input
                  id="sa-linux-user"
                  placeholder="martin"
                  value={newLinuxUsername}
                  onChange={(event) => setNewLinuxUsername(event.target.value.toLowerCase())}
                />
              </div>
              <div>
                <Label htmlFor="sa-pubkey">SSH public key (.pub line, optional — or let user self-enroll via link)</Label>
                <Input
                  id="sa-pubkey"
                  placeholder="ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAI... user@host"
                  value={newSshPublicKey}
                  onChange={(event) => setNewSshPublicKey(event.target.value)}
                />
              </div>
              <div className="space-y-2 pt-1">
                <div className="text-xs font-medium text-slate-700">Server permissions</div>
                {hosts.map((host) => (
                  <div key={host.id} className="flex items-center justify-between gap-2 rounded border border-slate-200 px-3 py-2">
                    <div>
                      <div className="text-xs font-medium text-slate-900">{host.display_name}</div>
                      <div className="text-[11px] text-slate-500">{host.host_key}</div>
                    </div>
                    <select
                      aria-label={`Permission tier for ${host.host_key}`}
                      className="h-9 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-900"
                      value={newGrantTierByHostId[host.id] ?? "none"}
                      onChange={(event) =>
                        setNewGrantTierByHostId((prev) => ({
                          ...prev,
                          [host.id]: event.target.value as ServerAccessTier | "none",
                        }))
                      }
                    >
                      <option value="none">No access</option>
                      <option value="investigate">Investigate (read-only + logs)</option>
                      <option value="operator">Operator (docker + services)</option>
                      <option value="admin">Admin (full sudo)</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <Button variant="secondary" size="sm" onClick={() => setShowAddPersonModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={
                  createPersonMutation.isPending ||
                  !newEmail.trim() ||
                  !newDisplayName.trim() ||
                  !newLinuxUsername.trim()
                }
                onClick={() => createPersonMutation.mutate()}
              >
                Provision & get instructions
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Paste / Replace SSH Key Modal */}
      {editingKeyPerson ? (
        <div
          data-hub-modal-overlay
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
        >
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="text-base font-semibold text-slate-900">
                SSH public key for {editingKeyPerson.display_name} ({editingKeyPerson.linux_username})
              </h2>
            </div>
            <div className="space-y-3 px-5 py-4">
              <Label htmlFor="replace-ssh-key">Single-line OpenSSH public key (.pub)</Label>
              <Input
                id="replace-ssh-key"
                placeholder="ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAI... martin@cloudkite.io"
                value={editingKeyInput}
                onChange={(event) => setEditingKeyInput(event.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <Button variant="secondary" size="sm" onClick={() => setEditingKeyPerson(null)}>
                Cancel
              </Button>
              {editingKeyPerson.ssh_public_key ? (
                <Button
                  variant="danger"
                  size="sm"
                  disabled={updatePersonMutation.isPending}
                  onClick={() =>
                    updatePersonMutation.mutate({
                      personId: editingKeyPerson.id,
                      clearSshPublicKey: true,
                    })
                  }
                >
                  Clear key
                </Button>
              ) : null}
              <Button
                variant="primary"
                size="sm"
                disabled={updatePersonMutation.isPending || !editingKeyInput.trim()}
                onClick={() =>
                  updatePersonMutation.mutate({
                    personId: editingKeyPerson.id,
                    sshPublicKey: editingKeyInput.trim(),
                  })
                }
              >
                Save public key
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Connection Instructions & Self-Service Invite Link Modal */}
      {instructionsModal ? (
        <div
          data-hub-modal-overlay
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
        >
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="text-base font-semibold text-slate-900">
                Connection instructions for {instructionsModal.person.display_name}
              </h2>
            </div>
            <div className="space-y-3 px-5 py-4">
              {instructionsModal.inviteUrl ? (
                <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-950">
                  <div className="font-semibold">Self-service SSH key enrollment & connection link (valid 72h):</div>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="flex-1 truncate rounded bg-white px-2 py-1 font-mono text-xs">
                      {instructionsModal.inviteUrl}
                    </code>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => copyToClipboard(instructionsModal.inviteUrl!, "Enrollment link")}
                    >
                      {copiedLabel === "Enrollment link" ? (
                        <Check className="mr-1 h-3.5 w-3.5" />
                      ) : (
                        <Copy className="mr-1 h-3.5 w-3.5" />
                      )}
                      Copy link
                    </Button>
                  </div>
                </div>
              ) : null}
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-700">Markdown connection guide</span>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => copyToClipboard(instructionsModal.instructions, "Connection instructions")}
                  >
                    <Copy className="mr-1 h-3.5 w-3.5" />
                    Copy instructions
                  </Button>
                </div>
                <pre className="max-h-80 overflow-y-auto whitespace-pre-wrap rounded border border-slate-200 bg-slate-950 p-3 font-mono text-xs text-slate-100">
                  {instructionsModal.instructions}
                </pre>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <Button variant="secondary" size="sm" onClick={() => setInstructionsModal(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Host Reconciler Bootstrap Modal */}
      {hostSetupModal ? (
        <div
          data-hub-modal-overlay
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
        >
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="text-base font-semibold text-slate-900">
                Install host reconciler on {hostSetupModal.hostKey}
              </h2>
            </div>
            <div className="space-y-3 px-5 py-4 text-xs text-slate-700">
              <div>
                Run this one-liner on <code className="font-mono font-semibold">{hostSetupModal.hostKey}</code> to write{" "}
                <code className="font-mono">/etc/sgs-access/agent.env</code> and enable the 60-second systemd timer:
              </div>
              <pre className="overflow-x-auto whitespace-pre-wrap rounded border border-slate-200 bg-slate-950 p-3 font-mono text-xs text-slate-100">
                {hostSetupModal.installCommand}
              </pre>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <Button variant="secondary" size="sm" onClick={() => setHostSetupModal(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => copyToClipboard(hostSetupModal.installCommand, "Bootstrap command")}
              >
                <Copy className="mr-1 h-3.5 w-3.5" />
                Copy bootstrap command
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
